import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import { StyleProps, WIDGET_ANIMATION_TYPE_HAS_DIRECTION } from '../../Interact/InteractOverlay/WidgetElement';

// Reference durations (ms) from the client's Lottie files, used as the defaults for
// pill/square so their timing matches the source.
export const WIDGET_ANIMATION_DEFAULT_DURATIONS: Partial<Record<string, number>> = {
	pillIn: 617,
	squareIn: 1017,
	pillOut: 467,
	squareOut: 650,
};

// Default length (ms) of the interact (click) animation - animate.css' own default.
const INTERACT_ANIMATION_DEFAULT_DURATION = 1000;

// On press the background flashes to the active colour over this long. It is a
// PAINT change (background-color / fill) so it is Safari-safe inside the foreignObject.
const ACTIVE_COLOR_FLASH_MS = 80;

// Non-scene-change clicks (see flashActiveState): how long the active colour is held
// before reverting, so the press feedback reads clearly even though the action itself
// (e.g. window.open, jumpToTime) runs synchronously and finishes instantly.
const ACTIVE_STATE_HOLD_MS = 500;

// IMPORTANT (Safari / WebKit foreignObject) - two separate rules:
// 1. WHOLE-WIDGET animation (pill/square scale, loop, slide, zoom) runs on the SVG
//    <g> (gElementRef), NEVER on the HTML content div. A `transform`/`opacity`
//    animation - or even `position: relative/absolute` - on HTML INSIDE the
//    foreignObject promotes it to a compositing layer that WebKit positions in the
//    wrong coordinate space, so the widget/content flies off. (getBoundingClientRect
//    still reports the correct box, so this is ONLY visible on real Safari.)
// 2. CONTENT sub-motion (text rises, icon slides in) runs on the text/icon HTML but
//    is animated with `margin` (layout) + `color`/`fill` (paint) ONLY - none of which
//    promote a compositing layer, so WebKit paints it in place.

type AnimEl = HTMLElement | SVGElement;

// Timing (ms) of an exit animation. Used to make other widgets exit when a widget whose
// action leaves the scene is clicked (see InteractOverlay's groupExit).
export type ExitTiming = { duration: number; delay: number };

const clearAnimation = (el: AnimEl | null | undefined) => {
	if (!el) { return; }
	Array.from(el.classList)
		.filter((c) => c.startsWith('animate__'))
		.forEach((c) => el.classList.remove(c));
	el.style.removeProperty('animation-delay');
	el.style.removeProperty('animation-duration');
	el.style.removeProperty('animation-fill-mode');
};

// Build the animate.css class for an entry/exit animation.
// - slideAndFade has NO animate.css keyframes -> map it to fadeIn<Dir> / fadeOut<Dir>.
// - A directional type with Direction "None" would build a keyframe-less class and just
//   appear -> fall back to Up on entry, Down on exit.
const resolveAnimationClass = (animation: string, direction: string, isEntry: boolean): string => {
	if (!animation) { return ''; }
	const dir = WIDGET_ANIMATION_TYPE_HAS_DIRECTION.includes(animation)
		? (direction || (isEntry ? 'Up' : 'Down'))
		: '';
	const name = animation === 'slideAndFade' ? (isEntry ? 'fadeIn' : 'fadeOut') : animation;
	return `animate__animated animate__${name}${dir}`;
};

const setAnimation = (
	el: AnimEl | null | undefined,
	animationClass: string,
	{ delay, duration }: { delay?: number; duration?: number } = {},
) => {
	if (!el) { return; }
	clearAnimation(el);
	if (!animationClass) { return; }
	animationClass.split(' ').filter(Boolean).forEach((c) => el.classList.add(c));
	if (delay != null) { el.style.setProperty('animation-delay', `${delay}ms`); }
	if (duration != null) { el.style.setProperty('animation-duration', `${duration}ms`); }
};

// Keep the widget <g> hidden until its entry animation actually STARTS (i.e. after the
// entry delay), then reveal it. Not every entry keyframe hides the widget at 0% -
// animate.css slideIn* is `visibility: visible` plus an offset - so without this the
// widget shows during the delay, before its entry has begun. `!important` so it wins
// over the animation's own `visibility` while the delay runs; `visibility` is a paint
// property, so it is Safari-safe inside the foreignObject. Reveals on the first
// animationstart inside the widget, or after the delay as a fallback (typewriter has no
// CSS animation). Returns a teardown: reveals immediately, or with show=false only stops
// waiting and leaves it hidden (a widget that never appeared must not show for its exit).
export const hideUntilEntryStarts = (el: AnimEl, delay: number): ((show?: boolean) => void) => {
	let timer: ReturnType<typeof setTimeout>;
	const teardown = (show = true) => {
		clearTimeout(timer);
		el.removeEventListener('animationstart', onStart);
		if (show) { el.style.removeProperty('visibility'); }
	};
	const onStart = (e?: Event) => {
		// an idle loop starting is not the entry (Text keeps its loop on the <g> while the
		// words/chars entry runs on the spans)
		if ((e?.target as Element | null)?.classList?.contains('animate__infinite')) { return; }
		teardown();
	};
	el.style.setProperty('visibility', 'hidden', 'important');
	el.addEventListener('animationstart', onStart);
	timer = setTimeout(() => teardown(), Math.max(0, delay) + 50);
	return teardown;
};

// pill/square are the only animations with a staggered content sub-motion.
const CONTENT_MOTION: Partial<Record<string, boolean>> = {
	pillIn: true,
	squareIn: true,
	pillOut: true,
	squareOut: true,
};

// On entry the content SNAPS visible after this fraction of the shape's duration, then
// glides slowly to its place over the REST of the window - finishing exactly when the
// shape does (so "content settled" == "animation done"). Text leads the icon slightly
// and travels further (30px vs 14px) so its glide reads clearly. Exit / unlisted
// animations start with the shape (lead 0). See applyContentMotion.
const CONTENT_ENTRY_STAGGER: Partial<Record<string, { icon: number; text: number }>> = {
	pillIn: { text: 0.34, icon: 0.42 },
};

export function useWidgetAnimation({
	gElementRef,
	textElementRef,
	iconElementRef,
	bgElementRef,
	style,
	isShowWidget = true,
	isExiting,
	onExitDone,
	iconSlideFrom = 'left',
	clickable = true,
	exitTiming,
	onClickExitStart,
}: {
	gElementRef: { current: SVGGElement | null | undefined };
	// Content sub-motion targets (pill/square only). Optional.
	textElementRef?: { current: HTMLElement | null | undefined };
	iconElementRef?: { current: HTMLElement | null | undefined };
	// Which side the icon slides in from. A right-anchored icon (button icon in a
	// right corner) must slide via margin-right, else the margin is absorbed and it
	// only fades in place. Left-anchored (default) slides via margin-left.
	iconSlideFrom?: 'left' | 'right';
	// Element whose colour flashes on press: a div flashes background-color, an <svg>
	// (icon) flashes fill.
	bgElementRef?: { current: AnimEl | null | undefined };
	style: StyleProps;
	// Whether the playhead is inside the widget's time window. Drives re-entry: the
	// entry animation re-plays every time this goes true, clearing any leftover
	// exit/press state so a widget that should be visible always shows.
	isShowWidget?: boolean;
	isExiting?: boolean;
	onExitDone?: () => void;
	// The interact (click) animation only plays when the widget actually reacts to a
	// click, i.e. it has an event linked (same rule as the pointer cursor). A widget
	// with no event just runs its onComplete and never animates.
	clickable?: boolean;
	// Overrides the exit's own duration/delay while isExiting (another widget was clicked and
	// its action leaves the scene, so this one exits right away with that widget's timing).
	exitTiming?: ExitTiming;
	// Passed only when the click's action leaves the scene (Go to scene, Go to URL in the same
	// window): the click then plays interact -> exit and this is called right at the click, with
	// the timing the other widgets exit with. Without it (pause, play, jump to time...) the widget
	// stays, so only interact plays.
	onClickExitStart?: (_timing: ExitTiming) => void;
}) {
	// Tears down a pending interact (timer + animationend listener) WITHOUT firing its
	// action, so it can be cancelled if the widget re-enters, hides or unmounts.
	const pressCancelRef = useRef<(() => void) | null>(null);
	// The resting colour to restore after a press flash. Captured ONCE when a press starts
	// from rest and reused by any overlapping press, so a rapid second press can never
	// capture the (already-active) colour and leave the button stuck on the active colour.
	const flashRestoreRef = useRef<{ el: AnimEl; prop: string; value: string } | null>(null);
	const isExitingRef = useRef(isExiting);
	isExitingRef.current = isExiting;
	const isShowWidgetRef = useRef(isShowWidget);
	isShowWidgetRef.current = isShowWidget;
	// via a ref: callers pass a new function every render, it must not rebuild the click handler
	const onClickExitStartRef = useRef(onClickExitStart);
	onClickExitStartRef.current = onClickExitStart;

	const restoreFlash = useCallback(() => {
		const restore = flashRestoreRef.current;
		if (!restore) { return; }
		if (restore.value) { restore.el.style.setProperty(restore.prop, restore.value); }
		else { restore.el.style.removeProperty(restore.prop); }
		restore.el.style.removeProperty('transition');
		flashRestoreRef.current = null;
	}, []);
	const cancelPress = useCallback(() => {
		pressCancelRef.current?.();
		pressCancelRef.current = null;
	}, []);
	// Reveals a widget still hidden while its entry delay runs (see hideUntilEntryStarts).
	const entryRevealRef = useRef<((show?: boolean) => void) | null>(null);
	const revealEntry = useCallback(() => {
		entryRevealRef.current?.();
		entryRevealRef.current = null;
	}, []);

	const {
		entryAnimation = '',
		entryAnimationDirection = '',
		entryAnimationDuration = 1000,
		entryAnimationDelay = 0,
		exitAnimation = '',
		exitAnimationDirection = '',
		exitAnimationDuration = 1000,
		exitAnimationDelay = 0,
		loopAnimation = '',
		loopAnimationDuration = 1000,
		loopAnimationDelay = 0,
		interactAnimation = '',
		interactAnimationDuration = INTERACT_ANIMATION_DEFAULT_DURATION,
	} = style.transformSettings ?? {};

	const activeBackgroundColor = style.backgroundSettings?.activeBackgroundColor;

	// Read via a ref so applyContentMotion stays stable: iconSlideFrom only affects
	// WHICH icon keyframe plays, it must never re-fire the entry/exit animation.
	const iconSlideFromRef = useRef(iconSlideFrom);
	iconSlideFromRef.current = iconSlideFrom;

	const entryAnimationClass = resolveAnimationClass(entryAnimation, entryAnimationDirection, true);
	const exitAnimationClass = resolveAnimationClass(exitAnimation, exitAnimationDirection, false);
	const loopAnimationClass = loopAnimation ? `animate__animated animate__${loopAnimation} animate__infinite` : '';

	// The shape (whole widget) animates the SVG <g> - see the Safari rules above.
	const shapeTarget = useCallback(() => gElementRef.current, [gElementRef]);

	// The icon fade animates `fill`, which must sit on the <svg> itself (the icon has
	// an inline colour, so the container's colour never reaches it).
	const iconAnimEl = useCallback(
		(): AnimEl | null | undefined => iconElementRef?.current?.querySelector('svg') ?? iconElementRef?.current,
		[iconElementRef],
	);

	const applyContentMotion = useCallback(
		(animation: string, shapeDuration: number, shapeDelay: number, isEntry: boolean) => {
			const textEl = textElementRef?.current;
			const iconEl = iconAnimEl();
			if (!CONTENT_MOTION[animation]) {
				clearAnimation(textEl);
				clearAnimation(iconEl);
				return;
			}
			const stag = isEntry ? CONTENT_ENTRY_STAGGER[animation] : undefined;
			// lead by `start`, then glide over the REST of the window so content finishes
			// exactly with the shape. exit / unlisted: start with the shape (lead 0).
			const timing = (start: number) => ({
				delay: Math.round(shapeDelay + shapeDuration * start),
				duration: Math.round(shapeDuration * (1 - start)),
			});
			const suffix = isEntry ? 'In' : 'Out';
			const iconSide = iconSlideFromRef.current === 'right' ? 'Right' : '';
			setAnimation(textEl, `animate__animated animate__contentText${suffix}`, timing(stag?.text ?? 0));
			setAnimation(iconEl, `animate__animated animate__contentIcon${suffix}${iconSide}`, timing(stag?.icon ?? 0));
		},
		[textElementRef, iconAnimEl],
	);

	// entry -> loop hand-off: when the entry animation ends, swap to loop
	useEffect(() => {
		const shapeEl = shapeTarget();
		const onAnimationEnd = (e: Event) => {
			if (!shapeEl || e.target !== shapeEl) { return; }
			// the exit's own animationend must not restore the loop (would flash the
			// widget back to full size before it unmounts)
			if (isExitingRef.current) { return; }
			// only the ENTRY hands off - the click sequence (interact -> exit) ends on this
			// <g> too and must not have the loop restored in the middle of it
			if (!entryAnimationClass || !entryAnimationClass.split(' ').every((c) => shapeEl.classList.contains(c))) { return; }
			if (loopAnimationClass) {
				setAnimation(shapeEl, loopAnimationClass, { delay: loopAnimationDelay, duration: loopAnimationDuration });
			}
		};
		shapeEl && shapeEl.addEventListener('animationend', onAnimationEnd);
		return () => {
			shapeEl && shapeEl.removeEventListener('animationend', onAnimationEnd);
		};
	}, [entryAnimationClass, loopAnimationClass, loopAnimationDelay, loopAnimationDuration, shapeTarget]);

	// entry animation - runs on mount AND every time the widget (re-)enters its time
	// window. Re-playing entry also RESETS the shape (clears any leftover exit/press
	// class), so a widget that should be visible always shows.
	// useLayoutEffect (NOT useEffect): the widget mounts when the playhead enters its
	// window, so it first renders in its REST state. A plain useEffect runs AFTER the
	// browser paints that rest frame -> the finished widget FLASHES for one frame before
	// the entry scale (scale 0) is applied. useLayoutEffect applies the animation before
	// paint, so the first painted frame is already the animation's start (scale 0).
	useLayoutEffect(() => {
		if (!isShowWidget) { return; }
		// a re-entry supersedes any press still winding down
		cancelPress();
		restoreFlash();
		const shapeEl = shapeTarget();
		revealEntry();
		if (entryAnimationClass) {
			setAnimation(shapeEl, entryAnimationClass, { delay: entryAnimationDelay, duration: entryAnimationDuration });
			// stay hidden until the entry starts, not just visible-and-waiting during the delay
			if (shapeEl && entryAnimationDelay > 0) {
				entryRevealRef.current = hideUntilEntryStarts(shapeEl, entryAnimationDelay);
			}
		} else if (loopAnimationClass) {
			setAnimation(shapeEl, loopAnimationClass, { delay: loopAnimationDelay, duration: loopAnimationDuration });
		} else {
			clearAnimation(shapeEl);
		}
		applyContentMotion(entryAnimation, entryAnimationDuration, entryAnimationDelay, true);
	}, [isShowWidget, entryAnimation, entryAnimationClass, entryAnimationDelay, entryAnimationDuration, loopAnimationClass, loopAnimationDelay, loopAnimationDuration, shapeTarget, applyContentMotion, cancelPress, restoreFlash, revealEntry]);

	// leaving the window (or unmounting) cancels a pending press so it can't fire
	// during the exit animation or after unmount and strand the widget.
	useEffect(() => {
		if (isShowWidget) { return; }
		cancelPress();
		restoreFlash();
		// left the window before the entry delay ran out: stop waiting, stay hidden
		entryRevealRef.current?.(false);
	}, [isShowWidget, cancelPress, restoreFlash]);
	useEffect(() => () => {
		cancelPress();
		entryRevealRef.current?.(false);
	}, [cancelPress]);

	// real exit (playhead passed the widget's end, or another widget's click leaves the scene)
	const exitDuration = exitTiming?.duration ?? exitAnimationDuration;
	const exitDelay = exitTiming?.delay ?? exitAnimationDelay;
	useEffect(() => {
		if (!isExiting) {
			return;
		}
		const shapeEl = shapeTarget();
		if (!shapeEl || !exitAnimationClass) {
			onExitDone?.();
			return;
		}

		// a pending press (e.g. its restore timer) must not bring the widget back mid-exit
		cancelPress();
		setAnimation(shapeEl, exitAnimationClass, { delay: exitDelay, duration: exitDuration });
		applyContentMotion(exitAnimation, exitDuration, exitDelay, false);

		let settled = false;
		let fallback: ReturnType<typeof setTimeout>;
		const settle = () => {
			if (settled) { return; }
			settled = true;
			clearTimeout(fallback);
			shapeEl.removeEventListener('animationend', handleEnd);
			onExitDone?.();
		};
		const handleEnd = (e: Event) => {
			if (e.target !== shapeEl) { return; }
			settle();
		};
		// Safety net in case animationend never fires. Must cover the delay + duration,
		// or a delayed exit is cut short and the widget just vanishes.
		fallback = setTimeout(settle, (exitDelay || 0) + (exitDuration || 1000) + 500);
		shapeEl.addEventListener('animationend', handleEnd);
		return () => {
			clearTimeout(fallback);
			shapeEl.removeEventListener('animationend', handleEnd);
		};
	}, [isExiting, exitAnimation, exitAnimationClass, exitDelay, exitDuration, onExitDone, shapeTarget, applyContentMotion, cancelPress]);

	// Play a one-shot animation class on the <g>, then call onEnd (on animationend, or a
	// safety timer if that never fires). Registered as the pending press, so cancelPress
	// tears it down WITHOUT calling onEnd.
	const playOnce = useCallback((el: AnimEl, animationClass: string, duration: number, onEnd: () => void) => {
		cancelPress();
		let timer: ReturnType<typeof setTimeout>;
		const handleEnd = (e: Event) => {
			if (e.target !== el) { return; }
			finish();
		};
		const teardown = () => {
			clearTimeout(timer);
			el.removeEventListener('animationend', handleEnd);
		};
		const finish = () => {
			teardown();
			pressCancelRef.current = null;
			onEnd();
		};
		setAnimation(el, animationClass, { delay: 0, duration });
		el.addEventListener('animationend', handleEnd);
		timer = setTimeout(finish, duration + 200);
		pressCancelRef.current = teardown;
	}, [cancelPress]);

	// Play the interact animation once on the <g> (flashing the active colour, if set),
	// then call onDone. Returns false when there is nothing to play.
	const playInteract = useCallback((onDone?: () => void): boolean => {
		const pressEl = shapeTarget();
		if (!interactAnimation || !pressEl) { return false; }

		const bgEl = bgElementRef?.current;

		// flash the colour to the active colour (paint change - Safari-safe), restored
		// on finish. A div flashes background-color; an <svg> icon flashes fill.
		const flashProp = bgEl && bgEl.tagName.toLowerCase() === 'svg' ? 'fill' : 'background-color';
		if (bgEl && activeBackgroundColor) {
			// Capture the resting value only if not already mid-flash (an overlapping press
			// must not record the active colour as the value to restore to).
			if (!flashRestoreRef.current) {
				flashRestoreRef.current = { el: bgEl, prop: flashProp, value: bgEl.style.getPropertyValue(flashProp) };
			}
			bgEl.style.setProperty('transition', `${flashProp} ${ACTIVE_COLOR_FLASH_MS}ms ease-out`);
			bgEl.style.setProperty(flashProp, activeBackgroundColor);
		}

		playOnce(pressEl, `animate__animated animate__${interactAnimation}`, interactAnimationDuration, () => {
			// restore the flashed colour to the resting value captured at the first press
			restoreFlash();
			onDone?.();
		});
		return true;
	}, [interactAnimation, interactAnimationDuration, activeBackgroundColor, shapeTarget, bgElementRef, playOnce, restoreFlash]);

	// Put the widget back at rest (loop, or no animation) with its content visible.
	const appearNow = useCallback(() => {
		const shapeEl = shapeTarget();
		revealEntry();
		if (loopAnimationClass) {
			setAnimation(shapeEl, loopAnimationClass, { delay: loopAnimationDelay, duration: loopAnimationDuration });
		} else {
			clearAnimation(shapeEl);
		}
		clearAnimation(textElementRef?.current);
		clearAnimation(iconAnimEl());
	}, [loopAnimationClass, loopAnimationDelay, loopAnimationDuration, shapeTarget, textElementRef, iconAnimEl, revealEntry]);
	const appearNowRef = useRef(appearNow);
	appearNowRef.current = appearNow;

	// Click: interact animation -> exit (out) animation -> action. Either animation is
	// skipped when not set; no linked event -> the action runs straight away. The exit only
	// plays when the action leaves the scene (onClickExitStart set), and its delay is ignored
	// (it is meant for the timed exit, a click should respond now).
	const triggerClickAnimation = useCallback((onComplete?: () => void) => {
		const el = shapeTarget();
		const clickExitClass = onClickExitStartRef.current ? exitAnimationClass : '';
		if (!clickable || !el || (!interactAnimation && !clickExitClass)) {
			onComplete?.();
			return;
		}
		// The action leaves the scene: the other widgets exit right now, while this one plays
		// interact -> exit. They take this widget's exit duration (its interact duration if it
		// has no exit), so they are gone before the action runs.
		if (onClickExitStartRef.current) {
			onClickExitStartRef.current?.({ duration: clickExitClass ? exitAnimationDuration : interactAnimationDuration, delay: 0 });
		}
		const runAction = () => {
			onComplete?.();
			// The exit played, so the action leaves the scene: stay gone like the other widgets.
			// Go to URL in the same window can take well over the beat below to unload the page.
			if (clickExitClass) { return; }
			// Interact only: the action leaves the widget on screen, so bring it back at rest -
			// after a beat, so it does not snap back mid-animation.
			const restoreTimer = setTimeout(() => {
				pressCancelRef.current = null;
				if (isShowWidgetRef.current && !isExitingRef.current) { appearNowRef.current(); }
			}, 500);
			pressCancelRef.current = () => clearTimeout(restoreTimer);
		};
		const runExit = () => {
			if (!clickExitClass) { runAction(); return; }
			applyContentMotion(exitAnimation, exitAnimationDuration, 0, false);
			playOnce(el, clickExitClass, exitAnimationDuration, runAction);
		};
		if (!playInteract(runExit)) { runExit(); }
	}, [clickable, interactAnimation, interactAnimationDuration, exitAnimation, exitAnimationClass, exitAnimationDuration, shapeTarget, playInteract, playOnce, applyContentMotion]);

	return { triggerClickAnimation };
}
